import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseApiService } from './base-api.service';
import { EventBus } from './event.bus';
import { EventTypeEnum, RequestTypeEnum } from '../../utils/enums';
import { LocalStorage } from './local.storage';
import { PromisesService } from './promises.service';
import {
    IAccountModel,
    IForgotPasswordModel,
} from '../../utils/interfaces';
import { environment } from 'src/environments/environment';
import { ILicense } from 'src/app/utils/interfaces/products-model';
import { setSpeechEngine, speechEngineFromTtsVariant } from 'src/app/core/speech-engine';

const ACCOUNT_INFO_KEY = 'ACCOUNT_INFO';

@Injectable({
    providedIn: 'root'
})
export class AccountService extends BaseApiService {
    private get baseUrl(): string {
        return environment.auth.apiUrl;
    }

    constructor(
        http: HttpClient,
        private eventBus: EventBus,
        private localStorage: LocalStorage,
        private promisesService: PromisesService) {
        super(http);

        this.eventBus.on(EventTypeEnum.LOGIN).subscribe(() => this.clear());
        this.eventBus.on(EventTypeEnum.TOKEN_REFRESHED).subscribe(() => this.clear());
    }

    public clear(): void {
        this.localStorage.remove(ACCOUNT_INFO_KEY);
    }

    public get userName(): string | null {
        const accountInfo = this.localStorage.get<IAccountModel>(ACCOUNT_INFO_KEY);

        return accountInfo?.username || null;
    }

    public get userLanguage(): string {
        const account = this.localStorage.get<IAccountModel>(ACCOUNT_INFO_KEY);

        return account?.language || '';
    }

    public get userId(): string {
        const account = this.localStorage.get<IAccountModel>(ACCOUNT_INFO_KEY);
        return account?.id || '';
    }

    public get email(): string {
        const account = this.localStorage.get<IAccountModel>(ACCOUNT_INFO_KEY);
        return account?.email || '';
    }

    public async getAccountInfo(): Promise<IAccountModel | null> {
        let account = this.localStorage.get<IAccountModel>(ACCOUNT_INFO_KEY);

        if (!account) {
            account = await this.promisesService.share(ACCOUNT_INFO_KEY, () => this.get<IAccountModel>(`${this.baseUrl}/api/v1/accounts/current`));
            if (account) {
                this.localStorage.set<IAccountModel>(ACCOUNT_INFO_KEY, {
                    id: account.id,
                    licenseIds: account.licenseIds,
                    roles: account.roles,
                    language: account.language,
                    username: account.username,
                    email: account.email,
                    firstName: account.firstName,
                    lastName: account.lastName,
                    ttsVariant: account.ttsVariant
                });
            }
        }

        setSpeechEngine(speechEngineFromTtsVariant(account?.ttsVariant));

        return account;
    }

    public forgotPassword(model: IForgotPasswordModel): Promise<void> {
        return this.post(`${this.baseUrl}/api/v1/accounts/resetpassword?userName=${model.username}`, null, null, RequestTypeEnum.ForgotPassword);
    }

    async getLicenses(id: number): Promise<ILicense> {
        return this.get<ILicense>(`${this.baseUrl}/api/v1/licenses/${id}`);
    }
}
